import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import PanelLayout from '../layouts/PanelLayout';
import { projectService } from '../../services/projectService';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import SearchableSelect from '../../components/common/SearchableSelect';
import DateInput, { toDisplayDate, toIsoDate } from '../../components/common/DateInput';
import IconInput from '../../components/common/IconInput';
import Loader from '../../components/Loader';

const STATUS_OPTIONS = [
   { id: 'ACTIVE', name: 'Active' },
   { id: 'COMPLETED', name: 'Completed' },
   { id: 'ON_HOLD', name: 'On Hold' },
   { id: 'CANCELLED', name: 'Cancelled' },
];

const emptyForm = {
   title: '',
   clientName: '',
   description: '',
   status: 'ACTIVE',
   techStack: '',
   githubUrl: '',
   liveUrl: '',
   startDate: '',
   endDate: '',
};

const SectionCard = ({ title, children }) => (
   <div className="bg-white dark:bg-dark-secondary rounded-xl border border-gray-200 dark:border-gray-700 p-6">
      {title && (
         <h2 className="text-xs font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-5">
            {title}
         </h2>
      )}
      {children}
   </div>
);

const Label = ({ children, required }) => (
   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
      {children} {required && <span className="text-red-500">*</span>}
   </label>
);

const ProjectForm = () => {
   const { id } = useParams();
   const navigate = useNavigate();
   const isEdit = !!id;
   const fileInputRef = useRef(null);

   const [loading, setLoading] = useState(isEdit);
   const [saving, setSaving] = useState(false);
   const [uploading, setUploading] = useState(false);
   const [formData, setFormData] = useState(emptyForm);
   const [logoPreview, setLogoPreview] = useState('');
   const [logoPath, setLogoPath] = useState('');

   useEffect(() => {
      if (isEdit) fetchProject();
   }, [id]);

   const fetchProject = async () => {
      try {
         setLoading(true);
         const res = await projectService.getById(id);
         const p = res.data;
         setFormData({
            title: p.title || '',
            clientName: p.clientName || '',
            description: p.description || '',
            status: p.status || 'ACTIVE',
            techStack: Array.isArray(p.techStack) ? p.techStack.join(', ') : '',
            githubUrl: p.githubUrl || '',
            liveUrl: p.liveUrl || '',
            startDate: toDisplayDate(p.startDate ? p.startDate.slice(0, 10) : ''),
            endDate: toDisplayDate(p.endDate ? p.endDate.slice(0, 10) : ''),
         });
         if (p.logoImage) {
            setLogoPreview(p.logoImage);
            try {
               const url = new URL(p.logoImage);
               setLogoPath(url.pathname.replace('/uploads/', ''));
            } catch {
               setLogoPath(p.logoImage.replace(/^.*\/uploads\//, ''));
            }
         }
      } catch {
         toast.error('Failed to load project');
         navigate('/admin/projects');
      } finally {
         setLoading(false);
      }
   };

   const set = (key) => (e) => setFormData((prev) => ({ ...prev, [key]: e.target.value }));

   const handleLogoUpload = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      try {
         setUploading(true);
         const res = await projectService.uploadLogo(file);
         setLogoPath(res.data.path);
         setLogoPreview(res.data.url);
      } catch {
         toast.error('Failed to upload logo');
      } finally {
         setUploading(false);
      }
   };

   const handleSubmit = async () => {
      if (!formData.title.trim()) {
         toast.error('Project title is required');
         return;
      }

      const payload = {
         title: formData.title.trim(),
         clientName: formData.clientName.trim() || null,
         description: formData.description.trim() || null,
         status: formData.status,
         techStack: formData.techStack
            ? formData.techStack
                 .split(',')
                 .map((t) => t.trim())
                 .filter(Boolean)
            : [],
         logoImage: logoPath || null,
         githubUrl: formData.githubUrl.trim() || null,
         liveUrl: formData.liveUrl.trim() || null,
         startDate: toIsoDate(formData.startDate),
         endDate: toIsoDate(formData.endDate),
      };

      try {
         setSaving(true);
         if (isEdit) {
            await projectService.update(id, payload);
            toast.success('Project updated successfully');
         } else {
            await projectService.create(payload);
            toast.success('Project created successfully');
         }
         navigate('/admin/projects');
      } catch (error) {
         const msg =
            error.response?.data?.errors?.map((e) => e.msg).join(', ') ||
            error.response?.data?.message ||
            'Failed to save project';
         toast.error(msg);
      } finally {
         setSaving(false);
      }
   };

   if (loading) {
      return (
         <PanelLayout>
            <Loader />
         </PanelLayout>
      );
   }

   const initials = formData.title
      ? formData.title
           .split(' ')
           .slice(0, 2)
           .map((w) => w[0])
           .join('')
           .toUpperCase()
      : null;

   return (
      <PanelLayout>
         <div className="space-y-6">
            {/* Page header */}
            <div className="flex items-center justify-between">
               <div>
                  <button
                     onClick={() => navigate('/admin/projects')}
                     className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-2 transition-colors"
                  >
                     <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                           strokeLinecap="round"
                           strokeLinejoin="round"
                           strokeWidth={2}
                           d="M15 19l-7-7 7-7"
                        />
                     </svg>
                     Projects
                  </button>
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                     {isEdit ? 'Edit Project' : 'New Project'}
                  </h1>
               </div>
               <div className="flex gap-3">
                  <Button
                     variant="outline"
                     onClick={() => navigate('/admin/projects')}
                     disabled={saving}
                  >
                     Cancel
                  </Button>
                  <Button onClick={handleSubmit} disabled={saving || uploading}>
                     {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Project'}
                  </Button>
               </div>
            </div>

            {/* Identity card */}
            <SectionCard title="Project Identity">
               <div className="flex gap-6">
                  {/* Logo upload zone */}
                  <div className="flex-shrink-0 flex flex-col justify-center">
                     <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                     />
                     <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="group relative w-[120px] h-[120px] rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 hover:border-[#0DCAF0] hover:bg-gray-100 dark:hover:bg-gray-700 transition-all overflow-hidden focus:outline-none focus:ring-2 focus:ring-[#0DCAF0]"
                     >
                        {uploading ? (
                           <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-gray-800/80">
                              <svg
                                 className="w-6 h-6 text-[#0DCAF0] animate-spin"
                                 fill="none"
                                 viewBox="0 0 24 24"
                              >
                                 <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                 />
                                 <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                 />
                              </svg>
                           </div>
                        ) : logoPreview ? (
                           <>
                              <img
                                 src={logoPreview}
                                 alt="Logo"
                                 className="w-full h-full object-contain p-2"
                              />
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1">
                                 <svg
                                    className="w-5 h-5 text-white"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                 >
                                    <path
                                       strokeLinecap="round"
                                       strokeLinejoin="round"
                                       strokeWidth={2}
                                       d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                                    />
                                 </svg>
                                 <span className="text-white text-xs font-medium">Change</span>
                              </div>
                           </>
                        ) : initials ? (
                           <>
                              <span className="text-3xl font-bold text-gray-400 dark:text-gray-500">
                                 {initials}
                              </span>
                              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1">
                                 <svg
                                    className="w-5 h-5 text-white"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                 >
                                    <path
                                       strokeLinecap="round"
                                       strokeLinejoin="round"
                                       strokeWidth={2}
                                       d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                                    />
                                 </svg>
                                 <span className="text-white text-xs font-medium">Upload</span>
                              </div>
                           </>
                        ) : (
                           <div className="flex flex-col items-center justify-center gap-2">
                              <svg
                                 className="w-7 h-7 text-gray-400 group-hover:text-[#0DCAF0] transition-colors"
                                 fill="none"
                                 stroke="currentColor"
                                 viewBox="0 0 24 24"
                              >
                                 <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={1.5}
                                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                                 />
                              </svg>
                              <span className="text-xs text-gray-400 group-hover:text-[#0DCAF0] transition-colors font-medium leading-tight text-center px-2">
                                 Upload Logo
                              </span>
                           </div>
                        )}
                     </button>
                     <p className="mt-2 text-center text-xs text-gray-400 dark:text-gray-500">
                        PNG, JPG, SVG
                     </p>
                  </div>

                  {/* Title, Client, Status */}
                  <div className="flex-1 flex flex-col gap-4">
                     <div>
                        <Label required>Title</Label>
                        <Input
                           value={formData.title}
                           onChange={set('title')}
                           placeholder="e.g. E-commerce Platform Redesign"
                        />
                     </div>
                     <div className="grid grid-cols-2 gap-4">
                        <div>
                           <Label>Client Name</Label>
                           <Input
                              value={formData.clientName}
                              onChange={set('clientName')}
                              placeholder="Acme Corp"
                           />
                        </div>
                        <div>
                           <SearchableSelect
                              label="Status"
                              searchable={false}
                              options={STATUS_OPTIONS}
                              value={formData.status}
                              onChange={(value) =>
                                 setFormData((prev) => ({ ...prev, status: value }))
                              }
                           />
                        </div>
                     </div>
                  </div>
               </div>
            </SectionCard>

            {/* About */}
            <SectionCard title="About">
               <div className="space-y-4">
                  <div>
                     <Label>Description</Label>
                     <textarea
                        value={formData.description}
                        onChange={set('description')}
                        placeholder="What is this project about? Goals, scope, key deliverables…"
                        rows={4}
                        className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-dark-primary px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#0DCAF0] focus:border-transparent resize-none"
                     />
                  </div>
                  <div>
                     <Label>Tech Stack</Label>
                     <Input
                        value={formData.techStack}
                        onChange={set('techStack')}
                        placeholder="React, Node.js, PostgreSQL, Docker  (comma-separated)"
                     />
                  </div>
               </div>
            </SectionCard>

            {/* Links */}
            <SectionCard title="Links">
               <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <IconInput
                     label="GitHub"
                     value={formData.githubUrl}
                     onChange={set('githubUrl')}
                     placeholder="github.com/org/repo"
                     icon={
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                           <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
                        </svg>
                     }
                  />
                  <IconInput
                     label="Live URL"
                     value={formData.liveUrl}
                     onChange={set('liveUrl')}
                     placeholder="yourproject.com"
                     icon={
                        <svg
                           className="w-4 h-4"
                           fill="none"
                           stroke="currentColor"
                           viewBox="0 0 24 24"
                        >
                           <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                           />
                        </svg>
                     }
                  />
               </div>
            </SectionCard>

            {/* Timeline */}
            <SectionCard title="Timeline">
               <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <DateInput
                     label="Start Date"
                     value={formData.startDate}
                     onChange={(val) => setFormData((prev) => ({ ...prev, startDate: val }))}
                  />
                  <DateInput
                     label="End Date"
                     value={formData.endDate}
                     onChange={(val) => setFormData((prev) => ({ ...prev, endDate: val }))}
                  />
               </div>
            </SectionCard>
         </div>
      </PanelLayout>
   );
};

export default ProjectForm;
